import { useAuth } from "@/contexts/AuthContext";
import { Link, NavLink, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  ClipboardList,
  MapPin,
  AlertTriangle,
  ShieldAlert,
  MessageSquareWarning,
  ScanLine,
  UserCog,
  CalendarDays,
  Heart,
  FileText,
  LogOut,
  ShieldCheck,
  Clock,
  Plug,
} from "lucide-react";

const NAV_BY_ROLE = {
  registered_manager: [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { to: "/service-users", label: "Service Users", icon: Users },
    { to: "/visits", label: "Visits", icon: MapPin },
    { to: "/rota", label: "Rota", icon: CalendarDays },
    { to: "/inbox", label: "Care Team Inbox", icon: MessageSquareWarning },
    { to: "/incidents", label: "Incidents", icon: AlertTriangle },
    { to: "/safeguarding", label: "Safeguarding", icon: ShieldAlert },
    { to: "/complaints", label: "Complaints", icon: MessageSquareWarning },
    { to: "/risk-assessments", label: "Risk Assessments", icon: ScanLine },
    { to: "/staff", label: "Staff", icon: UserCog },
    { to: "/dbs", label: "DBS Compliance", icon: ShieldCheck },
    { to: "/leave", label: "Leave Requests", icon: CalendarDays },
    { to: "/jibble", label: "Time & Attendance", icon: Clock },
    { to: "/integrations", label: "Integrations", icon: Plug },
    { to: "/reports", label: "Reports", icon: FileText },
    { to: "/settings/mfa", label: "Security (MFA)", icon: ShieldCheck },
  ],
  deputy_manager: [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { to: "/service-users", label: "Service Users", icon: Users },
    { to: "/visits", label: "Visits", icon: MapPin },
    { to: "/rota", label: "Rota", icon: CalendarDays },
    { to: "/inbox", label: "Care Team Inbox", icon: MessageSquareWarning },
    { to: "/incidents", label: "Incidents", icon: AlertTriangle },
    { to: "/safeguarding", label: "Safeguarding", icon: ShieldAlert },
    { to: "/complaints", label: "Complaints", icon: MessageSquareWarning },
    { to: "/risk-assessments", label: "Risk Assessments", icon: ScanLine },
    { to: "/staff", label: "Staff", icon: UserCog },
    { to: "/dbs", label: "DBS Compliance", icon: ShieldCheck },
    { to: "/leave", label: "Leave Requests", icon: CalendarDays },
    { to: "/jibble", label: "Time & Attendance", icon: Clock },
    { to: "/integrations", label: "Integrations", icon: Plug },
    { to: "/reports", label: "Reports", icon: FileText },
    { to: "/settings/mfa", label: "Security (MFA)", icon: ShieldCheck },
  ],
  team_leader: [
    { to: "/visits", label: "Today's Visits", icon: MapPin },
    { to: "/service-users", label: "Service Users", icon: Users },
    { to: "/rota", label: "Rota", icon: CalendarDays },
    { to: "/inbox", label: "Care Team Inbox", icon: MessageSquareWarning },
    { to: "/leave", label: "Leave", icon: CalendarDays },
    { to: "/incidents", label: "Incidents", icon: AlertTriangle },
    { to: "/risk-assessments", label: "Risk Assessments", icon: ScanLine },
    { to: "/settings/mfa", label: "Security (MFA)", icon: ShieldCheck },
  ],
  care_worker: [
    { to: "/visits", label: "Today's Visits", icon: MapPin },
    { to: "/service-users", label: "My Service Users", icon: Users },
    { to: "/rota", label: "My Rota", icon: CalendarDays },
    { to: "/inbox", label: "Family Messages", icon: MessageSquareWarning },
    { to: "/leave", label: "Leave", icon: CalendarDays },
    { to: "/incidents", label: "Incidents", icon: AlertTriangle },
    { to: "/settings/mfa", label: "Security (MFA)", icon: ShieldCheck },
  ],
  bank_worker: [
    { to: "/visits", label: "Today's Visits", icon: MapPin },
    { to: "/service-users", label: "My Service Users", icon: Users },
    { to: "/rota", label: "My Rota", icon: CalendarDays },
    { to: "/inbox", label: "Family Messages", icon: MessageSquareWarning },
    { to: "/leave", label: "Leave", icon: CalendarDays },
    { to: "/incidents", label: "Incidents", icon: AlertTriangle },
    { to: "/settings/mfa", label: "Security (MFA)", icon: ShieldCheck },
  ],
  nominated_individual: [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { to: "/service-users", label: "Service Users", icon: Users },
    { to: "/visits", label: "Visits", icon: MapPin },
    { to: "/rota", label: "Rota", icon: CalendarDays },
    { to: "/inbox", label: "Care Team Inbox", icon: MessageSquareWarning },
    { to: "/incidents", label: "Incidents", icon: AlertTriangle },
    { to: "/safeguarding", label: "Safeguarding", icon: ShieldAlert },
    { to: "/complaints", label: "Complaints", icon: MessageSquareWarning },
    { to: "/risk-assessments", label: "Risk Assessments", icon: ScanLine },
    { to: "/staff", label: "Staff", icon: UserCog },
    { to: "/dbs", label: "DBS Compliance", icon: ShieldCheck },
    { to: "/leave", label: "Leave Requests", icon: CalendarDays },
    { to: "/jibble", label: "Time & Attendance", icon: Clock },
    { to: "/integrations", label: "Integrations", icon: Plug },
    { to: "/reports", label: "Reports", icon: FileText },
    { to: "/settings/mfa", label: "Security (MFA)", icon: ShieldCheck },
  ],
  administrator: [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { to: "/service-users", label: "Service Users", icon: Users },
    { to: "/visits", label: "Visits", icon: MapPin },
    { to: "/rota", label: "Rota", icon: CalendarDays },
    { to: "/inbox", label: "Care Team Inbox", icon: MessageSquareWarning },
    { to: "/incidents", label: "Incidents", icon: AlertTriangle },
    { to: "/safeguarding", label: "Safeguarding", icon: ShieldAlert },
    { to: "/complaints", label: "Complaints", icon: MessageSquareWarning },
    { to: "/risk-assessments", label: "Risk Assessments", icon: ScanLine },
    { to: "/staff", label: "Staff", icon: UserCog },
    { to: "/dbs", label: "DBS Compliance", icon: ShieldCheck },
    { to: "/leave", label: "Leave Requests", icon: CalendarDays },
    { to: "/jibble", label: "Time & Attendance", icon: Clock },
    { to: "/integrations", label: "Integrations", icon: Plug },
    { to: "/reports", label: "Reports", icon: FileText },
    { to: "/settings/mfa", label: "Security (MFA)", icon: ShieldCheck },
  ],
  compliance_manager: [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { to: "/service-users", label: "Service Users", icon: Users },
    { to: "/incidents", label: "Incidents", icon: AlertTriangle },
    { to: "/safeguarding", label: "Safeguarding", icon: ShieldAlert },
    { to: "/inbox", label: "Care Team Inbox", icon: MessageSquareWarning },
    { to: "/complaints", label: "Complaints", icon: MessageSquareWarning },
    { to: "/risk-assessments", label: "Risk Assessments", icon: ScanLine },
    { to: "/staff", label: "Staff", icon: UserCog },
    { to: "/reports", label: "Reports", icon: FileText },
    { to: "/settings/mfa", label: "Security (MFA)", icon: ShieldCheck },
  ],
  family_member: [
    { to: "/family", label: "Family Portal", icon: Heart },
    { to: "/complaints", label: "Concerns", icon: MessageSquareWarning },
  ],
  super_admin: [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { to: "/service-users", label: "Service Users", icon: Users },
    { to: "/visits", label: "Visits", icon: MapPin },
    { to: "/incidents", label: "Incidents", icon: AlertTriangle },
    { to: "/safeguarding", label: "Safeguarding", icon: ShieldAlert },
    { to: "/inbox", label: "Care Team Inbox", icon: MessageSquareWarning },
    { to: "/staff", label: "Staff", icon: UserCog },
    { to: "/jibble", label: "Time & Attendance", icon: Clock },
    { to: "/integrations", label: "Integrations", icon: Plug },
    { to: "/reports", label: "Reports", icon: FileText },
  ],
};

export default function Sidebar({ open, onClose }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const nav = NAV_BY_ROLE[user?.role] || [];
  const roleLabel = (user?.role || "").replace(/_/g, " ");

  return (
    <>
      {/* Mobile backdrop */}
      <div
        className={`fixed inset-0 bg-black/40 z-30 md:hidden transition-opacity ${
          open ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={onClose}
        data-testid="sidebar-backdrop"
      />
      <aside
        className={`fixed left-0 top-0 h-full w-64 bg-[#0D7377] text-white z-40 flex flex-col transform transition-transform md:translate-x-0 ${
          open ? "translate-x-0" : "-translate-x-full md:translate-x-0"
        } no-print`}
      >
        <div className="px-6 pt-6 pb-4 border-b border-white/10">
          <div className="flex items-center gap-2.5">
            <div className="h-10 w-10 rounded-md bg-white flex items-center justify-center overflow-hidden shrink-0">
              <img src="/logo.png" alt="Rabah Concept" className="h-9 w-9 object-contain" data-testid="sidebar-brand-logo" />
            </div>
            <div>
              <h1 className="font-heading font-bold text-lg leading-tight" data-testid="sidebar-brand-name">Rabah Concept</h1>
              <p className="text-[11px] uppercase tracking-wider text-white/60">CQC compliant</p>
            </div>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-4 scrollbar-thin">
          <p className="px-3 pb-2 text-[11px] uppercase tracking-wider text-white/50 font-semibold">
            {roleLabel}
          </p>
          <ul className="space-y-1">
            {nav.map((item) => {
              const Icon = item.icon;
              const active = location.pathname.startsWith(item.to);
              return (
                <li key={item.to}>
                  <Link
                    to={item.to}
                    onClick={onClose}
                    data-testid={`sidebar-nav-${item.to.replace("/", "")}`}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                      active
                        ? "bg-white text-[#0D7377]"
                        : "text-white/85 hover:bg-white/10 hover:text-white"
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    <span>{item.label}</span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>

        <div className="px-3 py-3 border-t border-white/10">
          <NavLink
            to="/my-profile"
            data-testid="sidebar-my-profile"
            onClick={() => setMobileOpen(false)}
            className="flex items-center gap-3 px-3 py-2 mb-2 rounded-md hover:bg-white/10 transition-colors group"
          >
            <div className="h-9 w-9 rounded-full bg-white/15 flex items-center justify-center text-sm font-semibold group-hover:bg-white/25">
              {(user?.full_name || "?").split(" ").map((s) => s[0]).join("").slice(0, 2)}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium truncate">{user?.full_name}</p>
              <p className="text-xs text-white/60 truncate">Edit profile</p>
            </div>
          </NavLink>
          <button
            onClick={logout}
            data-testid="sidebar-logout-button"
            className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm bg-white/5 hover:bg-white/15 transition-colors"
          >
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </aside>
    </>
  );
}
